import { Card, Text } from "@backstage/ui";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode } from "react";

import { CONTENT_MAX_WIDTH } from "./contentWidth";

// The frame our own pages render in, instead of core-components' <Page>/<Header>; the admin page is on it.
// Anatomy and rules: docs/app/frontend-system.md § The page shell.

const useStyles = makeStyles((theme) => ({
	page: {
		maxWidth: CONTENT_MAX_WIDTH,
		margin: "0 auto",
		padding: theme.spacing(2, 3, 5),
		"@media (max-width: 640px)": { padding: theme.spacing(1.5, 1.5, 4) },
	},
	hero: {
		padding: theme.spacing(3, 0, 3),
		borderBottom: "1px solid var(--bui-border-1)",
		marginBottom: theme.spacing(3),
	},
	eyebrow: { textTransform: "uppercase", letterSpacing: "0.08em" },
	title: { margin: theme.spacing(0.5, 0, 1.5) },
	meta: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: theme.spacing(2) },
	section: { marginTop: theme.spacing(4) },
	heading: {
		display: "flex",
		alignItems: "baseline",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1.5),
		"@media (max-width: 640px)": { flexDirection: "column", alignItems: "flex-start" },
	},
	tiles: {
		display: "grid",
		gridTemplateColumns: "repeat(4, 1fr)",
		gap: theme.spacing(1.75),
		"@media (max-width: 960px)": { gridTemplateColumns: "repeat(2, 1fr)" },
		"@media (max-width: 640px)": { gridTemplateColumns: "1fr" },
	},
	state: {
		display: "grid",
		justifyItems: "center",
		gap: theme.spacing(1.5),
		padding: theme.spacing(6, 3),
		textAlign: "center",
	},
	footer: {
		marginTop: theme.spacing(5),
		paddingTop: theme.spacing(2),
		borderTop: "1px solid var(--bui-border-1)",
	},
}));

/** The page's `<main>`, centred at the shared content measure. */
export function PageShell(props: Readonly<{ children: ReactNode }>): ReactNode {
	const classes = useStyles();
	return <main className={classes.page}>{props.children}</main>;
}

/** The top of a page: where you are, one sentence about it, and an optional row of status and actions. */
export function PageHero(
	props: Readonly<{ eyebrow: string; title: ReactNode; meta?: ReactNode }>,
): ReactNode {
	const classes = useStyles();
	return (
		<header className={classes.hero}>
			<Text as="p" variant="body-small" color="secondary" className={classes.eyebrow}>
				{props.eyebrow}
			</Text>
			<Text as="h1" variant="title-large" className={classes.title}>
				{props.title}
			</Text>
			{props.meta ? <div className={classes.meta}>{props.meta}</div> : null}
		</header>
	);
}

/** A responsive row of tiles: four across, two on a tablet, one on a phone. */
export function TileGrid(props: Readonly<{ children: ReactNode; className?: string }>): ReactNode {
	const classes = useStyles();
	return (
		<div className={[classes.tiles, props.className].filter(Boolean).join(" ")}>
			{props.children}
		</div>
	);
}

/** A page section: a small eyebrow, the heading and a note on the right. */
export function PageSection(
	props: Readonly<{ eyebrow: string; title: string; note?: string; children: ReactNode }>,
): ReactNode {
	const classes = useStyles();
	return (
		// Labelled and focusable so a page can return focus to a section whose control went away.
		<section className={classes.section} aria-label={props.title} tabIndex={-1}>
			<div className={classes.heading}>
				<div>
					<Text
						as="div"
						variant="body-x-small"
						color="secondary"
						className={classes.eyebrow}
					>
						{props.eyebrow}
					</Text>
					<Text as="h2" variant="title-small">
						{props.title}
					</Text>
				</div>
				{props.note ? (
					<Text as="p" variant="body-small" color="secondary">
						{props.note}
					</Text>
				) : null}
			</div>
			{props.children}
		</section>
	);
}

/** A page-sized card for a read that failed or a page with nothing in it. */
export function StateCard(
	props: Readonly<{
		title: string;
		/** `alert` for a failure: it is announced, not just shown. */
		role?: "alert";
		children?: ReactNode;
		action?: ReactNode;
	}>,
): ReactNode {
	const classes = useStyles();
	return (
		<Card role={props.role}>
			<div className={classes.state}>
				<Text as="h2" variant="title-small">
					{props.title}
				</Text>
				{props.children ? (
					<Text as="p" color="secondary">
						{props.children}
					</Text>
				) : null}
				{props.action}
			</div>
		</Card>
	);
}

/** The quiet line under a page: what it is not, and where to go instead. */
export function PageFooter(props: Readonly<{ children: ReactNode }>): ReactNode {
	const classes = useStyles();
	return (
		<footer className={classes.footer}>
			<Text as="p" variant="body-small" color="secondary">
				{props.children}
			</Text>
		</footer>
	);
}
